// @react
import React, {
  useState,
  useRef,
  useEffect,
  ReactElement,
  useContext,
} from 'react'
// @types
import { AccountAccessFormValues } from 'modules/profile/types'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
import {
  GetUserQueryResult,
  UserUpdateEmailMutation,
  UserUpdateEmailMutationVariables,
  UserUpdatePasswordMutation,
  UserUpdatePasswordMutationVariables,
} from 'lib/graphql/graphqlTypes'
// @graphQL
import { useMutation } from '@apollo/client'
import { UPDATE_EMAIL, UPDATE_PASSWORD } from 'graphql/mutations/user.mutation'
// @common
import { SnackbarContext } from 'modules/common/context/snackbar'
// @libraries
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { DevTool } from '@hookform/devtools'
import { vsprintf } from 'sprintf-js'
import strings from 'lib/constants/strings'
// @components
import Grid from '@bit/atd.web.grid'
import TextLink from '@bit/atd.web.text-link'
import InputText from '@bit/atd.web.forms.input-text'
import InputPassword from '@bit/atd.web.forms.input-password'
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import EditAction from 'modules/profile/components/EditAction'
import EditingAction from 'modules/profile/components/EditingAction'
import { ExternalRouteNames } from 'constants/routeNames'
import { validationSchema } from 'modules/common/forms/validations/accountAccess'
import parse from 'html-react-parser'
import useEmailValidator from './email'
import Helper from 'utils/Helper.class'
import useDebounce from 'modules/common/hooks/useDebounce'
import useAsyncEffect from 'use-async-effect'
import makeStyles from '@material-ui/core/styles/makeStyles'
import colors from '@bit/atd.web.colors/dist/colors'

const useStyles = makeStyles(() => ({
  clientError: {
    '& > a': {
      color: colors.gray500,
    },
  },
}))

type PropsType = {
  data?: GetUserQueryResult['data']
  isLoading: boolean
}

const AccountAccessForm = ({ data, isLoading }: PropsType): ReactElement => {
  /******************************************
   * STATE
   ******************************************/

  const [editing, setEditing] = useState<boolean>(false)
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [validatorEmailMessage, setValidatorEmailMessage] = useState<string>('')

  /******************************************
   * REFS
   ******************************************/

  const emailRef = useRef<HTMLInputElement | null>(null)
  const passwordRef = useRef<HTMLInputElement | null>(null)
  const newPasswordRef = useRef<HTMLInputElement | null>(null)
  const newPasswordRetypeRef = useRef<HTMLInputElement | null>(null)
  /**
   * merge schemas together
   */

  const userData = data?.user

  const {
    moreValidationSchema,
    emailValidator,
    setValidatorEmail,
    emailValidatorData,
  } = useEmailValidator(data, validatorEmailMessage)
  const mergedSchema = Helper.merge(validationSchema, moreValidationSchema)
  /******************************************
   * HOOKS
   ******************************************/
  const classes = useStyles()
  const snackbar = useContext(SnackbarContext)

  const formMethods = useForm<AccountAccessFormValues>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(mergedSchema),
    defaultValues: {
      primaryEmailAddress: '',
      password: '',
      newPassword: '',
      newPasswordRetype: '',
      //      emailValidator: true,
    },
  })
  const {
    control,
    formState,
    reset,
    setError,
    clearErrors,
    trigger,
  } = formMethods
  const { errors, dirtyFields } = formState
  const email = formMethods.watch('primaryEmailAddress')
  const password = formMethods.watch('password')
  const newPasswordRetype = formMethods.watch('newPasswordRetype')
  const newPassword = formMethods.watch('newPassword')
  const isFormDirty = Object.keys(dirtyFields).length > 0

  const debouncedEmail = useDebounce(email, 100)

  useEffect(() => {
    reset({
      primaryEmailAddress: userData?.contactInfo?.primaryEmailAddress || '',
      password: '',
      newPassword: '',
      newPasswordRetype: '',
    })
  }, [reset, userData])

  useAsyncEffect(async () => {
    await trigger()
  }, [password, newPasswordRetype, newPassword])

  useEffect(() => {
    if (!editing || !dirtyFields.primaryEmailAddress) {
      clearErrors('primaryEmailAddress')
      return
    }
    if (emailValidatorData && !emailValidator?.isValid) {
      if (emailValidator?.message) {
        setValidatorEmailMessage(emailValidator.message)
        setError('primaryEmailAddress', {
          type: 'manual',
          message: emailValidator.message,
        })
      } else {
        setValidatorEmailMessage('Email address is not valid')
        setError('primaryEmailAddress', {
          type: 'manual',
          message: 'Email address is not valid',
        })
      }
    }
    if (emailValidatorData && emailValidator?.isValid) {
      clearErrors('primaryEmailAddress')
    }
  }, [emailValidator, setError, clearErrors, emailValidatorData])

  useEffect(() => {
    if (email !== '') {
      setValidatorEmail(email)
    }
  }, [debouncedEmail])

  /******************************************
   * GQL HOOKS
   ******************************************/

  const [updateUserPassword] = useMutation<
    UserUpdatePasswordMutation,
    UserUpdatePasswordMutationVariables
  >(UPDATE_PASSWORD)

  const [updateUserEmail] = useMutation<
    UserUpdateEmailMutation,
    UserUpdateEmailMutationVariables
  >(UPDATE_EMAIL)

  /******************************************
   * FUNCTIONS
   ******************************************/

  const onEditHandler = () => {
    setEditing(true)
  }

  /**
   *
   */
  const onCancelHandler = () => {
    setEditing(false)
    formMethods.clearErrors()
    formMethods.reset()
  }

  /**
   *
   * @param data
   */
  const onSubmitHandler = async (data: AccountAccessFormValues) => {
    if (!isFormDirty) {
      onCancelHandler()
      return
    }
    setIsSubmitting(true)
    await userUpdateHandler(data)
    setIsSubmitting(false)
    setEditing(false)
  }

  const userUpdateHandler = async (data: AccountAccessFormValues) => {
    if (dirtyFields.primaryEmailAddress) {
      const { errors: mutationErrors } = await updateUserEmail({
        variables: {
          input: {
            email: data.primaryEmailAddress,
          },
        },
      })
      if (!mutationErrors) {
        snackbar.setMessage({
          type: SnackbarTypes.SUCCESS,
          message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
            'Email',
          ]),
          show: true,
        })
      } else {
        snackbar.setMessage({
          type: SnackbarTypes.ERROR,
          message: strings.ERRORS.GENERIC_UPDATE_ERROR,
          show: true,
        })
        console.log('mutation error', mutationErrors)
      }
    }

    if (dirtyFields.newPassword) {
      const { errors: mutationErrors } = await updateUserPassword({
        variables: {
          input: {
            currentPassword: data.password,
            newPassword: data.newPassword,
          },
        },
      })

      if (!mutationErrors) {
        snackbar.setMessage({
          type: SnackbarTypes.SUCCESS,
          message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
            'Account Access',
          ]),
          show: true,
        })
      } else {
        snackbar.setMessage({
          type: SnackbarTypes.ERROR,
          message: strings.ERRORS.GENERIC_UPDATE_ERROR,
          show: true,
        })
        console.log('mutation error', mutationErrors)
      }
    }
  }

  /******************************************
   * RENDER
   ******************************************/

  const form = (
    <>
      {process.env.DEBUG === '1' && <DevTool control={control} />}

      <form>
        <Grid container spacing={4}>
          {/* Primary Email */}
          <Grid xs={12} sm={6} item>
            <Controller
              control={control}
              onFocus={() => emailRef?.current?.focus()}
              name="primaryEmailAddress"
              render={({ onChange, onBlur, value, name }) => (
                <InputText
                  //required
                  //disabled={editing}
                  name={name}
                  label={strings.PROFILE.ACCESS.PRIMARY_EMAIL}
                  placeholder={strings.PROFILE.ACCESS.PRIMARY_EMAIL_PLACEHOLDER}
                  type="email"
                  readOnly={!editing}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  inputRef={emailRef}
                  error={!!errors?.primaryEmailAddress}
                  errorText={
                    <span className={classes.clientError}>
                      {parse(errors?.primaryEmailAddress?.message || '')}
                    </span>
                  }
                />
              )}
            />
          </Grid>

          {/* Current Password */}
          <Grid xs={12} sm={6} item>
            <Controller
              control={control}
              onFocus={() => passwordRef?.current?.focus()}
              name="password"
              render={({ onChange, onBlur, value, name }) => (
                <InputPassword
                  required
                  hideToggle={!editing}
                  name={name}
                  label={strings.PROFILE.ACCESS.CURRENT_PASSWORD}
                  placeholder={
                    strings.PROFILE.ACCESS.CURRENT_PASSWORD_PLACEHOLDER
                  }
                  readOnly={!editing}
                  onBlur={() => editing && onBlur()}
                  onChange={onChange}
                  value={!editing ? '••••••••••••••' : value}
                  inputRef={passwordRef}
                  error={!!errors?.password}
                  errorText={errors?.password?.message}
                />
              )}
            />
          </Grid>

          {editing && (
            <>
              {/* New Password */}
              <Grid xs={12} sm={6} item>
                <Controller
                  control={control}
                  onFocus={() => newPasswordRef?.current?.focus()}
                  name="newPassword"
                  render={({ onChange, onBlur, value, name }) => (
                    <InputPassword
                      required
                      name={name}
                      label={strings.PROFILE.ACCESS.NEW_PASSWORD}
                      placeholder={
                        strings.PROFILE.ACCESS.NEW_PASSWORD_PLACEHOLDER
                      }
                      readOnly={!editing}
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value}
                      inputRef={newPasswordRef}
                      helperText={
                        <span>
                          {parse(strings.PROFILE.ACCESS.PASSWORD_STRENGTH_DESC)}
                        </span>
                      }
                      error={!!errors?.newPassword}
                      errorText={errors?.newPassword?.message}
                    />
                  )}
                />
              </Grid>

              {/* Retype New Password */}
              <Grid xs={12} sm={6} item>
                <Controller
                  control={control}
                  onFocus={() => newPasswordRetypeRef?.current?.focus()}
                  name="newPasswordRetype"
                  render={({ onChange, onBlur, value, name }) => (
                    <InputPassword
                      required
                      name={name}
                      label={strings.PROFILE.ACCESS.PASSWORD_RETYPE}
                      placeholder={
                        strings.PROFILE.ACCESS.PASSWORD_RETYPE_PLACEHOLDER
                      }
                      readOnly={!editing}
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value}
                      inputRef={newPasswordRetypeRef}
                      error={!!errors?.newPasswordRetype}
                      errorText={errors?.newPasswordRetype?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {/* Forgot Password Link */}
          <Grid xs={12} item>
            <TextLink
              variant="primary"
              href={ExternalRouteNames.SSO_RECOVER_PASSWORD}
            >
              {strings.PROFILE.ACCESS.FORGOT_PASSWORD}
            </TextLink>
          </Grid>
        </Grid>
      </form>
    </>
  )

  const box = (content: ReactElement): ReactElement => {
    return (
      <Box
        header={{
          title: !editing
            ? strings.PROFILE.ACCESS.TITLE
            : strings.PROFILE.ACCESS.TITLE_EDIT,
          actions: !editing ? (
            <EditAction disabled={isLoading} onClick={() => onEditHandler()} />
          ) : (
            <EditingAction
              bottom
              isSubmitting={isMutationSubmitting}
              onCancel={() => onCancelHandler()}
              onSubmit={formMethods.handleSubmit((data) =>
                onSubmitHandler(data),
              )}
            />
          ),
        }}
      >
        {content}
      </Box>
    )
  }

  return !isLoading ? box(form) : box(<SkeletonLoader />)
}

export default AccountAccessForm
